import { useState, useEffect } from 'react';
import Header from './components/layout/Header';
import PerformantMemberList from './components/members/PerformantMemberList';
import MapContainer from './components/map/MapContainer';
import FilterModal from './components/search/FilterModal';
import LoadingSpinner from './components/common/LoadingSpinner';
import MessageManager from './components/common/MessageManager';
import { useMembers } from './hooks/useMembers';
import { useFilters } from './hooks/useFilters';
import { useResponsive } from './hooks/useResponsive';
import { useMapSync } from './hooks/useMapSync';
import { useFavorites } from './hooks/useFavorites';

function App({ config }) {
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [mobileView, setMobileView] = useState('list'); // 'list' or 'map'
    
    const { isMobile, isTablet } = useResponsive();
    const { filters, updateFilter, clearFilters } = useFilters();
    const { 
        members, 
        loading, 
        error, 
        searchTerm, 
        setSearchTerm, 
        loadMembers 
    } = useMembers(config, filters);

    const {
        visibleMembers,
        handleMapBoundsChange,
        handleMemberHover,
        handleMemberHoverOut,
        handleMarkerHover,
        handleMarkerHoverOut,
        registerMapInstance,
        registerMarker,
        unregisterMarker,
        registerMemberList,
        updateVisibleMembers
    } = useMapSync();

    // Get current user ID from config
    const currentUserId = config?.currentUserId || null;

    const {
        loading: favoritesLoading,
        error: favoritesError,
        loadFavorites,
        toggleFavorite,
        isFavorite,
        isPending: isPendingFavorite,
        clearError: clearFavoritesError
    } = useFavorites(currentUserId);

    // Determine if user is logged in - user_id is 0 when not logged in
    const isLoggedIn = Boolean(currentUserId) && currentUserId !== '0' && currentUserId !== 0;

    useEffect(() => {
        // Check if filters are being applied (not just cleared)
        const hasActiveFilters = Object.keys(filters).length > 0;
        loadMembers(hasActiveFilters);
    }, [filters, searchTerm]);

    // Load favorites when currentUserId becomes available
    useEffect(() => {
        if (currentUserId) {
            loadFavorites();
        }
    }, [currentUserId, loadFavorites]);

    // Update visible members when members change or on initial load
    useEffect(() => {
        updateVisibleMembers(members, null); // null bounds means show all members initially
    }, [members, updateVisibleMembers]);

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleFilterChange = (newFilters) => {
        // Clear all filters first
        clearFilters();
        
        // Apply new filters - newFilters should be {fieldId: value}
        Object.keys(newFilters).forEach(fieldId => {
            if (newFilters[fieldId]) {
                updateFilter(fieldId, newFilters[fieldId]);
            }
        });
        
        setShowFilterModal(false);
    };

    const handleClearFilters = () => {
        clearFilters();
        setSearchTerm('');
    };

    if (loading) {
        return (
            <div className="gw-bp-geosearch-container">
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="gw-bp-geosearch-container">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    return (
        <div className="gw-bp-geosearch-container">
            <div className={`gw-bp-geosearch-content ${isMobile ? 'mobile' : ''}`}>
                {isMobile ? (
                    // Mobile: Show either list or map based on toggle
                    <>
                        <Header 
                            searchTerm={searchTerm}
                            onSearch={handleSearch}
                            onShowFilters={() => setShowFilterModal(true)}
                            onClearFilters={handleClearFilters}
                            hasActiveFilters={Object.keys(filters).some(key => filters[key])}
                            mobileView={mobileView}
                            onMobileViewChange={setMobileView}
                        />
                        {mobileView === 'list' ? (
                            <PerformantMemberList 
                                members={members}
                                loading={loading}
                                config={config}
                                onMemberHover={handleMemberHover}
                                onMemberHoverOut={handleMemberHoverOut}
                                registerMemberList={registerMemberList}
                                isFavorite={isFavorite}
                                onToggleFavorite={toggleFavorite}
                                isPendingFavorite={isPendingFavorite}
                                isLoggedIn={isLoggedIn}
                                enableVirtualization={true}
                                performanceMode="auto"
                            />
                        ) : (
                            <MapContainer 
                                members={members}
                                config={config}
                                height="calc(100vh - 120px)"
                                onMarkerHover={handleMarkerHover}
                                onMarkerHoverOut={handleMarkerHoverOut}
                                onBoundsChange={handleMapBoundsChange}
                                registerMapInstance={registerMapInstance}
                                registerMarker={registerMarker}
                                unregisterMarker={unregisterMarker}
                                isFavorite={isFavorite}
                                onToggleFavorite={toggleFavorite}
                                isPendingFavorite={isPendingFavorite}
                                isLoggedIn={isLoggedIn}
                            />
                        )}
                    </>
                ) : (
                    // Desktop: Side-by-side layout
                    <>
                        <div className="left-panel">
                            <Header 
                                searchTerm={searchTerm}
                                onSearch={handleSearch}
                                onShowFilters={() => setShowFilterModal(true)}
                                onClearFilters={handleClearFilters}
                                hasActiveFilters={Object.keys(filters).some(key => filters[key])}
                            />
                            <PerformantMemberList 
                                members={visibleMembers}
                                loading={loading}
                                config={config}
                                onMemberHover={handleMemberHover}
                                onMemberHoverOut={handleMemberHoverOut}
                                registerMemberList={registerMemberList}
                                isFavorite={isFavorite}
                                onToggleFavorite={toggleFavorite}
                                isPendingFavorite={isPendingFavorite}
                                isLoggedIn={isLoggedIn}
                                enableVirtualization={true}
                                performanceMode="auto"
                                showResultCount={true}
                                totalMembers={members.length}
                            />
                        </div>
                        <div className="right-panel">
                            <MapContainer 
                                members={members}
                                config={config}
                                height="100%"
                                onMarkerHover={handleMarkerHover}
                                onMarkerHoverOut={handleMarkerHoverOut}
                                onBoundsChange={(bounds) => handleMapBoundsChange(bounds, members)}
                                registerMapInstance={registerMapInstance}
                                registerMarker={registerMarker}
                                unregisterMarker={unregisterMarker}
                                isFavorite={isFavorite}
                                onToggleFavorite={toggleFavorite}
                                isPendingFavorite={isPendingFavorite}
                                isLoggedIn={isLoggedIn}
                            />
                        </div>
                    </>
                )}
            </div>

            {showFilterModal && (
                <FilterModal
                    config={config}
                    currentFilters={filters}
                    onApply={handleFilterChange}
                    onClose={() => setShowFilterModal(false)}
                />
            )}

            <MessageManager />
        </div>
    );
}

export default App;
